//React dependencies
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

//Action dependencies
import { addEkedEquipment, updateEkedEquipment, deleteEkedEquipment, getEkedEquipments, addEkedEquipmentFiles, getEkedEquipmentFiles, deleteEkedEquipmentFiles, getEkedEquipmentFilePreviews } from "../../store/actions/EkedEquipmentActions";

//Component dependencies
import LoadingBackdrop from "../../components/LoadingBackdrop/LoadingBackdrop";
import TableButtons from "../../components/Button/TableButtons";
import Modal from "../../components/Modal/Modal";
import AddButton from "../../components/AddButton/AddButton";
import SearchBar from "../../components/SearchBar/SearchBar";
import FileUploaderPopup from "../../components/Popup/FileUploaderPopup";
import DocumentPopup from "../../components/Popup/DocumentPopup";
import { commonSort, getFormDataFromFiles, getSelectedFileNames } from "../../common";
import ImagePreviewPopup from "../../components/Popup/ImagePreviewPopup";

//MUI dependencies
import { Grow, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Tooltip, IconButton, ListItemIcon } from "@mui/material";
import { CloudUpload, CloudDownload, PictureAsPdfRounded, AttachFile } from "@mui/icons-material";
//Other NPM dependencies
import { StyledTableDiv } from "../../components/StyledComponents/StyledComponents";
import { isMobile } from "react-device-detect";

function EkedEquipment() {
    const { t } = useTranslation("translation");
    const dispatch = useDispatch();

    //Sorting states
    const [sortOrder, setSortOrder] = useState(0);
    const [sortColumn, setSortColumn] = useState("");

    //Pagination states
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [editable, setEditable] = useState(false);
    const [disableButton, setDisableButton] = useState(false);
    const [editableRow, setEditableRow] = useState("");
    const [initialRows, setInitialRows] = useState([]);
    const [isModalOpen, setModalOpen] = useState(false);
    const [deletedRow, setDeletedRow] = useState([]);
    let [rows, setRows] = useState([]);
    const [rowsLength, setRowsLength] = useState(0);
    const [idsOnPage, setIdsOnPage] = useState([]);

    const { EkedEquipment } = useSelector((EkedEquipment) => EkedEquipment);

    useEffect(() => {
        LoadDataHandler();
    }, []);

    useEffect(() => {
        if (EkedEquipment?.ekedEquipmentList?.resultObject) {
            if (EkedEquipment.ekedEquipmentList.resultObject.length > 0) {
                const resultObject = EkedEquipment.ekedEquipmentList.resultObject
                setRows(Object.values(resultObject));
                setInitialRows(cloneObject(Object.values(resultObject)));
                setRowsLength(resultObject.length);
                setEditableRow(false);
                setEditable(false);
                setDisableButton(false);

                if(resultObject.length !== idsOnPage.length){
                    setIdsOnPage(EkedEquipment.ekedEquipmentList.resultObject.map(element => element.id))
                }
            } else {
                setRows([]);
            }
        }
        if (EkedEquipment?.isTaskUpdated) {
            LoadDataHandler();
        }
        if (EkedEquipment?.ekedEquipmentFiles) {
            setEquipmentFiles(EkedEquipment.ekedEquipmentFiles);
            setIsDocumentPopupOpen(true);
            EkedEquipment.ekedEquipmentFiles = null;
            handleSearch(searchText)
        }
        if (EkedEquipment?.ekedEquipmentFilePreviews && EkedEquipment?.ekedEquipmentFilePreviews.length !== 0) {
            setFilePreview(EkedEquipment?.ekedEquipmentFilePreviews)
        }
    }, [EkedEquipment]);

    useEffect(() => {
        if(idsOnPage.length > 0){
            dispatch(getEkedEquipmentFilePreviews(idsOnPage));
        }
    }, [idsOnPage]);

    // Load data handler
    const LoadDataHandler = () => {
        dispatch(getEkedEquipments());
        setSearchText("");
    };

    const [searchText, setSearchText] = useState("");
    const handleSearch = (value) => {
        setSearchText(value.toLowerCase());
        let filteredItems = cloneObject(initialRows).filter((item) => item.name.toLowerCase().includes(value) || item.id.toString().toLowerCase().includes(value));
        setRows(filteredItems);
    };

    const handleSort = (row, column) => {
        setSortOrder(commonSort(row, column)[0]);
        setSortColumn(commonSort(row, column)[1]);
        commonSort(row, column);
    };

    //pagination
    const HandleChangePage = (_event, newPage) => {
        setCurrentPage(newPage);
    };

    const HandleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setCurrentPage(0);
    };

    const [name, setName] = useState("");
    const NameHandler = (event) => {
        setName(event.target.value);
    };

    const cloneObject = (obj) => {
        return JSON.parse(JSON.stringify(obj));
    };

    const enableEditHandler = (row) => {
        setName(row.name);
        setEditableRow(row.id);
        setEditable(true);
        setDisableButton(true);
    };

    const disableEditHandler = () => {
        setName("");
        setEditable(false);
        //enable edit button
        setDisableButton(false);
        handleSearch(searchText);
    };

    const updateAddHandler = (row) => {
        if (row.id != "") {
            let data = {
                id: row.id,
                name: name,
            };
            dispatch(updateEkedEquipment(data));
        } else {
            setDisableButton(false);
            let data = {
                name: name,
            };
            dispatch(addEkedEquipment(data));
        }

        if (name !== "") {
            LoadDataHandler();
            setName("");
        }
    };

    const addBlankRowHandler = () => {
        setCurrentPage(0);
        setDisableButton(true);

        let addedBlankRow = [
            {
                id: "",
                name: "",
            },
        ];

        rows = addedBlankRow.concat(rows);
        setEditableRow(addedBlankRow[0].id);
        setEditable(true);
        setRows(rows);
    };

    const deleteHandler = (row) => {
        toggleModal();
        dispatch(deleteEkedEquipment(row.id));
    };

    const deleteButtonHandler = (row) => {
        setDeletedRow(row);
        toggleModal();
    };

    const toggleModal = () => {
        setModalOpen((prevState) => !prevState);
    };

    const renderTableCell = (rowItems, isSortingAction = true) => {
        let directionType = sortOrder === 1 ? "asc" : "desc";
        return rowItems.map((item, index) => {
            if (item.id === "Actions") {
                return (
                    <TableCell key={index} id={`eked_rowItems_TableCell_Actions_${index}_${item.id}`} align="right">
                        {t("Actions")}
                    </TableCell>
                );
            } else {
                return isSortingAction ? (
                    <TableCell key={index} id={`eked_rowItems_TableCell_${index}_${item.id}`} align="center">
                        <TableSortLabel
                            id={`eked_rowItems_TableSortLabel_${index}_${item.id}`}
                            active={sortColumn === item.id}
                            direction={directionType}
                            onClick={() => handleSort(rows, item.id)}
                            style={{ paddingLeft: 25 }}
                        >
                            {item.label}
                        </TableSortLabel>
                    </TableCell>
                ) : (
                    <TableCell align="center">{item.label}</TableCell>
                );
            }
        });
    };

    // File Management
    const [equipmentFiles, setEquipmentFiles] = useState([]);
    const [equipmentIdForFileUpload, setEquipmentIdForFileUpload] = useState(0);
    const [equipmentIdForFileDelete, setEquipmentIdForFileDelete] = useState(0);
    const [isFileUploaderPopupOpen, setIsFileUploaderPopupOpen] = useState(false);
    const [isDocumentPopupOpen, setIsDocumentPopupOpen] = useState(false);

    const HandleDocumentPopupOpen = (id) => {
        setEquipmentIdForFileDelete(id);
        dispatch(getEkedEquipmentFiles(id));
    };

    const HandleFileUploaderPopupOpen = (id) => {
        setEquipmentIdForFileUpload(id);
        setIsFileUploaderPopupOpen(true);
    };

    const UploadFiles = (files) => {
        const formData = getFormDataFromFiles(files);
        const payload = {
            equipmentId: equipmentIdForFileUpload,
            data: formData,
        };
        dispatch(addEkedEquipmentFiles(payload));
        setIsFileUploaderPopupOpen(false);
    };

    const DeleteEquipmentFile = (selectedFiles) => {
        const selectedFileNames = getSelectedFileNames(selectedFiles);
        const payload = {
            equipmentId: equipmentIdForFileDelete,
            fileNames: selectedFileNames,
        };
        dispatch(deleteEkedEquipmentFiles(payload));
        setIsDocumentPopupOpen(false);
    };


    const [filePreview, setFilePreview] = useState([]);

    const renderFilePreview = (rowId) => {
        if(filePreview.length > 0){
            const file = filePreview.find(element => element.name.split(".")[0] === rowId.toString())
            if(file){
                const fileExtension = file.name.split(".").pop();
                if (["jpg", "jpeg", "png", "gif",'PNG',"JPEG"].includes(fileExtension)) {
                    return (
                        <img
                            id={`docp_Img_preview_${file.name}`}
                            width={80}
                            height={80}
                            alt={file.name}
                            src={window.URL.createObjectURL(file.blob)}
                            onClick={() => handleClickImage(file)}
                            style={{ cursor: "pointer" }}
                        />
                    );
                } else if (["pdf"].includes(fileExtension)) {
                    return <PictureAsPdfRounded id={`docp_Pdf_preview_${file.name}`} fontSize="large" />;
                } else {
                    return <AttachFile id={`docp_AttachFile_preview_${file.name}`} fontSize="large" />;
                }
            }
          

        }
    };

    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState();
    const handleClickImage = (image) => {
        setPreviewImage(image);
        setIsImagePreviewOpen(true);
    };

 
    return (
        <StyledTableDiv>
            <h1>{t("EkedEquipments")}</h1>

            <AddButton onClick={addBlankRowHandler} disabled={disableButton} />

            <Grid container marginBottom={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Grid item xs={0} sm={0} md={4}></Grid>
                <Grid item xs={12} sm={12} md={4}>
                    <SearchBar value={searchText} onChange={(newValue) => setSearchText(newValue)} onRequestSearch={(value) => handleSearch(value)} />
                </Grid>
                <Grid item xs={0} sm={0} md={4}></Grid>
            </Grid>

            <Grow in={true} style={{ transformOrigin: "0 0 0" }} {...{ timeout: 1000 }}>
                <TableContainer id="eked_TableContainer" component={Paper}>
                    <Table id="eked_Table">
                        <TableHead id="eked_TableHead">
                            <TableRow id="eked_TableRow">
                                {isMobile
                                    ? renderTableCell([
                                          { id: "name", label: t("Name") },
                                          { id: "Actions", label: t("Actions") },
                                          { id: "UploadFile", label: t("UploadFile") },
                                          { id: "ImgPreview", label: t("ImagePreview") },
                                      ])
                                    : renderTableCell([
                                          { id: "id", label: t("ID") },
                                          { id: "name", label: t("Name") },
                                          { id: "Actions", label: t("Actions") },
                                          { id: "UploadFile", label: t("UploadFile") },
                                          { id: "ImgPreview", label: t("ImagePreview")},
                                      ])}
                            </TableRow>
                        </TableHead>
                        <TableBody id="eked_TableBody">
                            {rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage).map((row, index) => (
                                <TableRow id={`eked_TableRow_${index}`} key={index}>
                                    {!isMobile ? (
                                        <TableCell id={`eked_TableCell_${index}`} align="center" width="20%">
                                            {row.id}
                                        </TableCell>
                                    ) : null}
                                    <TableCell id={`eked_TableCell_1_${index}`} align="center" width="50%">
                                        {editable && editableRow === row.id ? (
                                            <TextField
                                                id={`eked_TableCell_1_TextField_${index}`}
                                                fullWidth
                                                onInput={(e) => NameHandler(e)}
                                                defaultValue={name}
                                                variant="outlined"
                                                inputProps={{
                                                    maxLength: 50,
                                                }}
                                            />
                                        ) : (
                                            row.name
                                        )}
                                    </TableCell>
                                    <TableButtons
                                        width="30%"
                                        editable={editable}
                                        editableRow={editableRow}
                                        row={row}
                                        updateAddHandler={() => updateAddHandler(row)}
                                        enableEditHandler={() => enableEditHandler(row)}
                                        disableEditHandler={() => disableEditHandler()}
                                        deleteButtonHandler={() => deleteButtonHandler(row)}
                                    />
                                        <TableCell id={`eked__TableCell_2_${index}`} align="center">
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Tooltip title={t("UploadFile")}>
                                                    <IconButton id={`eked_TableCell_IconButton_${index}`}  onClick={() => HandleFileUploaderPopupOpen(row.id)}>
                                                        <CloudUpload />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Tooltip title={t("ShowFiles")}>
                                                    <IconButton id={`eked_TableCell_IconButton_1_${index}`} onClick={() => HandleDocumentPopupOpen(row.id)}>
                                                        <CloudDownload />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                    <TableCell id={`eked__TableCell_2_${index}`} align="center">
                                        <Grid container spacing={1}>
                                            <ListItemIcon id={`docp_ListItemIcon_${index}`} sx={{marginLeft:"60px"}}>{renderFilePreview(row.id)} </ListItemIcon>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        id="eked_TablePagination"
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        component="div"
                        count={rowsLength}
                        rowsPerPage={rowsPerPage}
                        page={currentPage}
                        onPageChange={HandleChangePage}
                        onRowsPerPageChange={HandleChangeRowsPerPage}
                    />
                </TableContainer>
            </Grow>

            <Modal title={t("Delete")} content={t("WillYouDoTransaction")} approved={() => deleteHandler(deletedRow)} isModalOpen={isModalOpen} toggle={toggleModal} />
            {isImagePreviewOpen ? <ImagePreviewPopup isImagePreviewOpen={isImagePreviewOpen} previewImage={previewImage} handleClose={() => setIsImagePreviewOpen(false)} /> : null}
            {isFileUploaderPopupOpen ? <FileUploaderPopup isOpen={isFileUploaderPopupOpen} onCancel={() => setIsFileUploaderPopupOpen(false)} onConfirm={UploadFiles} /> : null}
            {isDocumentPopupOpen === true ? (
                <DocumentPopup
                    id="eked_documentPopup"
                    isMaintenanceDocuments={false}
                    isEquipmentDocuments={true}
                    list={[]}
                    equipmentDocuments={equipmentFiles}
                    open={isDocumentPopupOpen}
                    deleteFile={null}
                    deleteEquipmentFiles={DeleteEquipmentFile}
                    close={() => setIsDocumentPopupOpen(false)}
                />
            ) : null}
            <LoadingBackdrop id="eked_LoadingBackdrop" />
        </StyledTableDiv>
    );
}

export default EkedEquipment;
